/**
 * Parsing and formatting of InfluxDB / Flux duration literals.
 *
 * Flux durations are a sequence of magnitude-unit pairs, e.g. `1h30m`, `7d`, `90s`.
 * We deliberately support only the *fixed-length* units. Flux also has `mo` and `y`,
 * but those are calendar durations whose length depends on when they are evaluated,
 * which makes them unusable both for aligned window arithmetic and for a storage
 * model. Where users think in months or years we ask for days instead.
 */

/** Seconds per supported Flux duration unit, longest first for greedy formatting. */
const UNITS: ReadonlyArray<readonly [unit: string, seconds: number]> = [
  ['w', 604800],
  ['d', 86400],
  ['h', 3600],
  ['m', 60],
  ['s', 1],
  ['ms', 0.001],
];

/** Lookup used by the parser. Order matters: `ms` must be tried before `m`. */
const PARSE_UNITS: ReadonlyArray<readonly [unit: string, seconds: number]> = [
  ['ms', 0.001],
  ['w', 604800],
  ['d', 86400],
  ['h', 3600],
  ['m', 60],
  ['s', 1],
];

const TOKEN = /(\d+(?:\.\d+)?)(ms|[wdhms])/g;

/** Sentinel used for "keep forever" retention, matching InfluxDB's `0s`. */
export const INFINITE = Infinity;

export class DurationError extends Error {
  constructor(input: string, reason: string) {
    super(`Invalid duration ${JSON.stringify(input)}: ${reason}`);
    this.name = 'DurationError';
  }
}

/**
 * Parse a Flux duration literal into seconds.
 *
 * `0`, `0s` and `inf` all mean "infinite retention" and return {@link INFINITE};
 * InfluxDB itself spells an infinite bucket retention as `0s`.
 *
 * @throws {DurationError} if the literal is empty, malformed, or uses a calendar unit.
 */
export function parseDuration(input: string): number {
  const raw = input.trim().toLowerCase();
  if (raw === '') throw new DurationError(input, 'empty');
  if (raw === 'inf' || raw === '0' || raw === '0s') return INFINITE;
  if (/\d\s*(mo|y)/.test(raw)) {
    throw new DurationError(input, 'calendar units (mo, y) are not supported — use days');
  }

  TOKEN.lastIndex = 0;
  let total = 0;
  let consumed = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN.exec(raw)) !== null) {
    if (match.index !== consumed) break;
    const unit = PARSE_UNITS.find(([u]) => u === match![2]);
    /* c8 ignore next -- the regex can only produce known units */
    if (!unit) throw new DurationError(input, `unknown unit ${match[2]}`);
    total += Number(match[1]) * unit[1];
    consumed = match.index + match[0].length;
  }
  if (consumed !== raw.length)
    throw new DurationError(input, 'expected a form like 30s, 5m, 1h30m, 7d');
  if (total <= 0) throw new DurationError(input, 'must be greater than zero');
  return total;
}

/** Parse a duration, returning `null` instead of throwing. */
export function tryParseDuration(input: string): number | null {
  try {
    return parseDuration(input);
  } catch {
    return null;
  }
}

/**
 * Parse a duration where zero means zero.
 *
 * A bucket retention of `0s` means "keep forever" — that is InfluxDB's own
 * convention. A task offset or a late-data tolerance of `0s` means nothing of the
 * sort, so those fields go through this instead of {@link parseDuration}.
 *
 * @throws {DurationError} on malformed input.
 */
export function parseSpan(input: string): number {
  const raw = input.trim().toLowerCase();
  if (raw === '' || raw === '0' || raw === '0s') return 0;
  const parsed = parseDuration(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Parse a span, returning `null` instead of throwing. */
export function tryParseSpan(input: string): number | null {
  try {
    return parseSpan(input);
  } catch {
    return null;
  }
}

/**
 * Render seconds back as the shortest exact Flux duration literal.
 *
 * `formatDuration(5400)` → `"1h30m"`. {@link INFINITE} renders as `"0s"`, which is
 * how InfluxDB expresses an infinite bucket retention.
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '0s';
  if (seconds <= 0) return '0s';
  let rest = seconds;
  let out = '';
  for (const [unit, size] of UNITS) {
    const n = Math.floor(rest / size + 1e-9);
    if (n > 0) {
      out += `${n}${unit}`;
      rest -= n * size;
    }
  }
  return out === '' ? '0s' : out;
}

/** Human-friendly duration for prose and diagram labels, e.g. `"90 days"`. */
export function humanDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return 'forever';
  const plural = (n: number, word: string) => `${round(n)} ${word}${round(n) === 1 ? '' : 's'}`;
  if (seconds >= 604800 && seconds % 604800 === 0) return plural(seconds / 604800, 'week');
  if (seconds >= 86400) return plural(seconds / 86400, 'day');
  if (seconds >= 3600) return plural(seconds / 3600, 'hour');
  if (seconds >= 60) return plural(seconds / 60, 'minute');
  return plural(seconds, 'second');
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}

/** True when `a` is an exact whole-number multiple of `b`. */
export function divides(b: number, a: number): boolean {
  if (b <= 0 || !Number.isFinite(a) || !Number.isFinite(b)) return false;
  const q = a / b;
  return Math.abs(q - Math.round(q)) < 1e-9;
}

/** Round `seconds` up to the next whole multiple of `multiple`. */
export function ceilToMultiple(seconds: number, multiple: number): number {
  if (multiple <= 0) return seconds;
  return Math.ceil(seconds / multiple - 1e-9) * multiple;
}
